'use client';

import { useState, useEffect } from 'react';
import Script from 'next/script';
import { MapLocatorProps } from './types';
import { toDocumentWithEmbeddedEntries } from 'libs/contentful/toDocumentWithEmbeddedEntries';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from '../shared/RichTextCopy/RichTextCopy';
import { cn, getColorway } from 'utils/styles';
import { MapLocatorMap } from './MapLocatorMap';
import { MapLocatorPopup } from './MapLocatorPopup';
import { MapLocatorAddressForm } from './MapLocatorAddressForm';
import { SelectedLocation } from './types';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { getOptanonCategoryClass, ONETRUST_CATEGORIES } from 'libs/oneTrust';
import { Margins } from '@/types/constants';
import { getEntryNativeFields } from 'utils/entryNativeFields';
import { getModuleSectionName } from 'utils/modules';

declare global {
  interface Window {
    onChimeGoogleMapsReady?: () => void;
  }
}

// `loading=async` fires the script's load event before google.maps exists, so the API's own
// `callback` is the only readiness signal. Register at module scope: the tag ships in the HTML.
const MAPS_READY_CALLBACK = 'onChimeGoogleMapsReady';

let resolveMapsReady: (() => void) | undefined;
const googleMapsReady = new Promise<void>(resolve => {
  resolveMapsReady = resolve;
});

if (typeof window !== 'undefined') {
  window[MAPS_READY_CALLBACK] = () => resolveMapsReady?.();
}

export default function MapLocator(props: MapLocatorProps) {
  const { sys } = props;
  const { headline, copy, color, inputPlaceholder, margins } = getEntryNativeFields<MapLocatorProps['fields']>(props);
  const sectionName = getModuleSectionName(props);

  const headlineDoc = headline
    ? toDocumentWithEmbeddedEntries(headline, { parentTypename: 'MapLocator', fieldName: 'headline' })
    : undefined;
  const copyDoc = copy
    ? toDocumentWithEmbeddedEntries(copy, { parentTypename: 'MapLocator', fieldName: 'copy' })
    : undefined;

  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [showPopupAnimation, setShowPopupAnimation] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);

  useEffect(() => {
    googleMapsReady.then(() => setMapsReady(true));
  }, []);

  useEffect(() => {
    if (selectedLocation && !hasShownPopup) {
      // oxlint-disable-next-line react/set-state-in-effect -- triggers animation state when location changes
      setShowPopupAnimation(true);
      setHasShownPopup(true);
      const timeout = setTimeout(() => setShowPopupAnimation(false), 600); // match animation duration
      return () => clearTimeout(timeout);
    }
  }, [selectedLocation, hasShownPopup]);

  const handleSearch = (address: string) => {
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          setUserCoords({ lat: loc.lat(), lng: loc.lng() });
        }
      });
    }
  };

  return (
    <>
      <Script
        className={getOptanonCategoryClass(ONETRUST_CATEGORIES.STRICTLY_NECESSARY)}
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&loading=async&callback=${MAPS_READY_CALLBACK}`}
        strategy="afterInteractive"
        data-testid="google-maps-script"
      />
      <section
        {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
        className={`flex ${getColorway(color)}`}>
        <div className={cn('content-wrapper', margins === Margins.NoMargins ? 'py-0' : 'all-margins')}>
          <div className="flex flex-col-reverse sm:flex-row h-full">
            <div className="sm:basis-1/2 flex flex-col justify-between h-full min-h-0 sm:px-14 pt-8 pb-12 ml:py-20">
              <div className="flex flex-col flex-1 min-h-0">
                <div className="font-sans font-semibold text-heading-lg" {...liveUpdateAttrs(sys.id, 'headline')}>
                  {headlineDoc && <RichTextHeadline headline={headlineDoc} />}
                </div>

                <div
                  className="font-serif text-[18px] mt-4 grow min-h-0 overflow-auto"
                  {...liveUpdateAttrs(sys.id, 'copy')}>
                  <RichTextCopy copy={copyDoc} />
                </div>
              </div>

              <div className="relative mt-8 sm:mt-0 w-full" {...liveUpdateAttrs(sys.id, 'inputPlaceholder')}>
                <MapLocatorAddressForm onSearch={handleSearch} inputPlaceholder={inputPlaceholder} />
              </div>
            </div>

            <div className="relative basis-1/2 -mx-6 sm:mx-0 md:h-[531px] sm:h-[350px]">
              <MapLocatorMap
                mapsReady={mapsReady}
                onLocationSelect={setSelectedLocation}
                userCoords={userCoords}
                setUserCoords={setUserCoords}
              />
              {selectedLocation && (
                <MapLocatorPopup
                  location={selectedLocation}
                  userCoords={userCoords}
                  showAnimation={showPopupAnimation}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
